import * as Sentry from '@sentry/react';
import { consoleInfoLog, isNonEmptyString } from 'App/Helpers';

import { captureCustomException } from '../../ErrorTracking/captureCustomException';

const gameanalytics = require('gameanalytics');

// gameanalytics evaluates every event the moment it is queued, but only marks
// itself enabled once the /init response arrives, so events sent in between are
// discarded with "SDK is disabled".
// See https://github.com/GameAnalytics/GA-SDK-JAVASCRIPT/issues/398
const PENDING_EVENTS_LIMIT = 100;
const SDK_READY_POLL_MS = 150;
// After the report deadline the SDK may still come up (a backgrounded tab runs
// timers about once a second), so polling continues, just less often.
const SDK_LATE_POLL_MS = 1000;
// How long the SDK may take before the slow start is reported. Events stay
// queued past it, capped by PENDING_EVENTS_LIMIT, so it costs nothing to wait.
const SDK_READY_TIMEOUT_MS = 30000;

/**
 * Whether the SDK would accept an event right now.
 *
 * `isRemoteConfigsReady()` looks like the obvious signal but it is sticky from
 * the first /init and says nothing about the current session, so it stays true
 * after `endSession()` and through a re-init. The SDK's own three-part check
 * is what every `add*Event` runs against, so mirror that instead. `GAState` is
 * reachable on the package export but absent from its typings - fall back to
 * the public flag if a future release stops exposing it.
 *
 * @returns {boolean}
 */
const sdkAcceptsEvents = () => {
    const state = gameanalytics.state?.GAState;

    if (typeof state?.isInitialized !== 'function')
        return gameanalytics.GameAnalytics.isRemoteConfigsReady();

    return Boolean(state.isInitialized() && state.isEnabled() && state.sessionIsStarted());
};

const storeKey = (gameKey, name) => `GA::${gameKey}::${name}`;

/**
 * Drops events the SDK recorded but has not transmitted yet.
 *
 * Withdrawing consent has to stop transmission, and the SDK keeps unsent
 * events in `localStorage` across page loads - leaving them there would ship
 * data after the user opted out. The in-memory copy goes too, because the SDK
 * writes that back out on its next save.
 *
 * `ga_items` is deliberately left alone: it holds `default_user_id`, and
 * clearing it resets GameAnalytics user identity and inflates user counts if
 * consent is granted again.
 *
 * @param {string} gameKey - The GameAnalytics game key.
 * @returns {void}
 */
const purgeStoredEvents = (gameKey) => {
    const store = gameanalytics.store?.GAStore?.instance;

    if (store) {
        store.eventsStore = [];
        store.sessionsStore = [];
    }

    try {
        window.localStorage.removeItem(storeKey(gameKey, 'ga_event'));
        window.localStorage.removeItem(storeKey(gameKey, 'ga_session'));
    } catch {
        // Storage can be unavailable (private mode, blocked cookies). The
        // in-memory clear above is the half that stops this page sending.
    }
};

/**
 * Reports a problem to Sentry.
 *
 * @param {string} message
 * @param {{ level?: 'error'|'warning', tags?: Record<string, string> }} [options]
 *   `tags` are for low-cardinality values only: tags can be filtered and
 *   counted across events in Sentry, contexts cannot.
 * @returns {void}
 */
const logWarning = (message, { level = 'error', tags } = {}) => {
    Sentry.withScope((scope) => {
        scope.setLevel(level);
        scope.setTag('app', 'ga_events');
        if (tags) scope.setTags(tags);
        Sentry.captureException(new Error(message));
    });
};

/**
 * Buckets the wait so it can be a tag. The report fires at the timeout, so
 * anything past it means timers ran late (a hidden or throttled tab).
 *
 * @param {number|undefined} elapsedMs
 * @returns {'<35s'|'35-60s'|'60s+'|'unknown'}
 */
const elapsedBucket = (elapsedMs) => {
    if (elapsedMs === undefined) return 'unknown';
    if (elapsedMs < 35000) return '<35s';
    if (elapsedMs < 60000) return '35-60s';

    return '60s+';
};

/**
 * Parses a design event's custom fields.
 *
 * Unity supplies these as a JSON string, so a malformed payload must not take
 * the event down with it - report it and send the event without fields, since
 * the event itself is worth more than the annotation.
 *
 * @param {string} source - Event source name.
 * @param {string} landingName - The App name.
 * @param {string} eventName - Event name, for the report.
 * @param {string} [fieldsJson] - Additional fields as JSON string.
 * @returns {Object|undefined}
 */
const parseCustomFields = (source, landingName, eventName, fieldsJson) => {
    if (!isNonEmptyString(fieldsJson)) return undefined;

    let parsed;

    try {
        parsed = JSON.parse(fieldsJson);
    } catch {
        parsed = undefined;
    }

    if (parsed && typeof parsed === 'object') return parsed;

    logWarning(
        `[Game Analytics ${landingName}]: ${source} Design event "${eventName}" has invalid fieldsJson, sending without custom fields`
    );

    return undefined;
};

const areProgressionParamsValid = (source, status, progression1, progression2, progression3) => {
    const hasStatus = ['Start', 'Fail', 'Complete', 'Undefined'].includes(status);
    const hasP1 = isNonEmptyString(progression1);
    const hasP2 = isNonEmptyString(progression2);
    const hasP3 = isNonEmptyString(progression3);

    if (hasP3 && (!hasP2 || !hasP1 || !hasStatus)) {
        const message = `[Game Analytics ${source}]: Progression event invalid: Missing ${!hasStatus ? 'status ' : ''}${!hasP1 ? 'progression1 ' : ''}${!hasP2 ? 'progression2' : ''}`;

        logWarning(message);
        return false;
    }
    if (hasP2 && (!hasP1 || !hasStatus)) {
        const message = `[Game Analytics ${source}]: Progression event invalid: Missing ${!hasStatus ? 'status ' : ''}${!hasP1 ? 'progression1' : ''}`;

        logWarning(message);
        return false;
    }
    if (hasP1 && !hasStatus) {
        const message = `[Game Analytics ${source}]: Progression event invalid: Missing status`;

        logWarning(message);
        return false;
    }

    return true;
};

const areResourceParamsValid = (source, flowType, currency, amount, itemType, itemId) => {
    if (!['Source', 'Sink', 'Undefined'].includes(flowType)) {
        const message = `[Game Analytics ${source}]: Resource event invalid: flowType parameter "${flowType}" is incorrect`;

        logWarning(message);
        return false;
    }
    if (!/^[A-Za-z]{1,64}$/.test(currency)) {
        const message = `[Game Analytics ${source}]: Resource event invalid: currency parameter "${currency}" is incorrect`;

        logWarning(message);
        return false;
    }
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
        const message = `[Game Analytics ${source}]: Resource event invalid: amount parameter is incorrect ${amount} -> flowType: ${flowType}, currency: ${currency}, itemType: ${itemType}`;

        logWarning(message);
        return false;
    }
    if (!/^[A-Za-z0-9]{1,64}$/.test(itemType)) {
        const message = `[Game Analytics ${source}]: Resource event invalid: itemType parameter "${itemType}" is incorrect`;

        logWarning(message);
        return false;
    }
    if (typeof itemId !== 'string' || itemId.trim().length === 0) {
        const message = `[Game Analytics ${source}]: Resource event invalid: itemId parameter "${itemId}" is incorrect`;

        logWarning(message);
        return false;
    }

    return true;
};

/**
 * The SDK calls the service can make, keyed by queue entry.
 *
 * Queueing the SDK call rather than the public method keeps validation on the
 * caller's timeline and stops a replay from re-entering the queue.
 */
const SDK_SENDERS = {
    design: (eventName, value, customFields) =>
        gameanalytics.GameAnalytics.addDesignEvent(eventName, value, customFields),
    progression: (status, progression1, progression2, progression3, score) =>
        gameanalytics.GameAnalytics.addProgressionEvent(
            gameanalytics.EGAProgressionStatus[status],
            progression1,
            progression2,
            progression3,
            score
        ),
    resource: (flowType, currency, amount, itemType, itemId) =>
        gameanalytics.GameAnalytics.addResourceEvent(
            gameanalytics.EGAResourceFlowType[flowType],
            currency,
            amount,
            itemType,
            itemId
        ),
    ad: (eventType, adType, adSdkName, adPlacement, customFields) =>
        gameanalytics.GameAnalytics.addAdEvent(
            gameanalytics.EGAAdAction[eventType],
            gameanalytics.EGAAdType[adType],
            adSdkName,
            adPlacement,
            customFields
        ),
    error: (errorType, message) =>
        gameanalytics.GameAnalytics.addErrorEvent(
            gameanalytics.EGAErrorSeverity[errorType],
            message
        ),
    startSession: () => gameanalytics.GameAnalytics.startSession(),
    endSession: () => gameanalytics.GameAnalytics.endSession(),
    dimension1: (value) => gameanalytics.GameAnalytics.setCustomDimension01(value),
    dimension2: (value) => gameanalytics.GameAnalytics.setCustomDimension02(value),
    dimension3: (value) => gameanalytics.GameAnalytics.setCustomDimension03(value),
};

/**
 * GameAnalyticsService
 *
 * Service for integrating GameAnalytics into the application.
 * Provides methods for sending various analytics events and managing game sessions.
 *
 * Events sent between `init()` and the SDK's init response are queued and
 * replayed in order once the SDK accepts them. A slow start is reported once
 * but the wait goes on, so a late start still delivers the queue.
 */
export default class GameAnalyticsService {
    /**
     * Creates a new GameAnalyticsService instance.
     *
     * @param {Object} params - Service configuration parameters.
     * @param {string} params.gameKey - The GameAnalytics game key.
     * @param {string} params.gameSecret - The GameAnalytics secret key.
     * @param {"production"|"development"|"local"} params.environment - The environment mode.
     * @param {string} params.landingName - The App name.
     * @param {string} params.build - The game build version.
     * @param {{ currencies?: string[], itemTypes?: string[] }} [params.resources] - Available currencies and item types.
     * @param {{ values1?: { preset: string[], current: string }, values2?: { preset: string[], current: string }, values3?: { preset: string[], current: string }}} [params.dimensions] - Custom dimensions for events.
     * @param {boolean} [params.immediateInit] - Indicates whether the analytics service should be initialized when the class is created.
     * @param {string?} [params.userId] - Unique identifier for the user.
     */
    constructor({
        gameKey,
        gameSecret,
        environment,
        landingName,
        build,
        resources,
        dimensions,
        userId,
        immediateInit = true,
    }) {
        this.gameKey = gameKey;
        this.gameSecret = gameSecret;
        this.environment = environment;
        this.build = build;
        this.landingName = landingName;
        this.resources = resources;
        this.dimensions = dimensions;
        this.immediateInit = immediateInit;
        this.userId = userId || undefined;
        this.initFailed = false;
        this.sdkReady = false;
        this.pendingEvents = [];
        this.droppedEvents = 0;
        this.sdkReadyPollId = undefined;
        this.sdkReadyTimeoutId = undefined;
        this.sdkWaitStartedAt = undefined;
        this.beforeUnloadListener = undefined;

        if (this.immediateInit) this.init();
    }

    /**
     * Sets a specific property on the service.
     *
     * @param {string} valueName - The property name.
     * @param {*} value - The value to assign.
     * @returns {void}
     */
    setState(valueName, value) {
        this[valueName] = value;
    }

    /**
     * Initializes GameAnalytics with the provided configuration.
     *
     * @returns {boolean}
     */
    init() {
        try {
            gameanalytics.GameAnalytics.setEnabledVerboseLog(this.environment !== 'production');

            // Set app custom dimensions
            const { values1, values2, values3 } = this.dimensions || {};

            if (values1) {
                gameanalytics.GameAnalytics.configureAvailableCustomDimensions01(values1.preset);
                gameanalytics.GameAnalytics.setCustomDimension01(values1.current);
            }
            if (values2) {
                gameanalytics.GameAnalytics.configureAvailableCustomDimensions02(values2.preset);
                gameanalytics.GameAnalytics.setCustomDimension02(values2.current);
            }
            if (values3) {
                gameanalytics.GameAnalytics.configureAvailableCustomDimensions03(values3.preset);
                gameanalytics.GameAnalytics.setCustomDimension03(values3.current);
            }
            // Set game resource values
            const { currencies, itemTypes } = this.resources || {};

            if (currencies)
                gameanalytics.GameAnalytics.configureAvailableResourceCurrencies(currencies);
            if (itemTypes)
                gameanalytics.GameAnalytics.configureAvailableResourceItemTypes(itemTypes);

            if (this.userId) gameanalytics.GameAnalytics.configureUserId(this.userId);

            gameanalytics.GameAnalytics.configureBuild(this.build);
            // Submission is a global SDK flag that {@link stop} leaves off, so
            // a service built after consent is granted again has to turn it
            // back on; it is a no-op on a first init.
            gameanalytics.GameAnalytics.setEnabledEventSubmission(true);
            this.watchSdkReady();
            gameanalytics.GameAnalytics.initialize(this.gameKey, this.gameSecret);
            consoleInfoLog('## Init: Game Analytics', 'init');

            return true;
        } catch (error) {
            this.setState('initFailed', true);

            if (this.environment === 'production')
                captureCustomException(error, 'GameAnalyticsInitFailed');

            return false;
        }
    }

    /**
     * Waits for the SDK to start accepting events, then flushes the queue.
     *
     * Polls rather than listening: the remote configs listener fires before
     * the SDK assigns a session id and also fires for outcomes that leave the
     * SDK disabled, so it needs re-checking anyway. One mechanism against the
     * SDK's own 100ms task cadence is simpler than a listener plus a tick.
     *
     * @returns {void}
     */
    watchSdkReady() {
        if (sdkAcceptsEvents()) {
            this.sdkReady = true;
            return;
        }

        // A last check on the way out: the SDK runs this while its `isUnloading`
        // flag is set, which is the one path where it records events
        // synchronously instead of deferring them to its own task queue. Events
        // that land here are persisted and go out on the next page load.
        this.beforeUnloadListener = {
            onBeforeUnload: () => this.checkSdkReady(),
        };
        gameanalytics.GameAnalytics.addOnBeforeUnloadListener(this.beforeUnloadListener);

        this.sdkWaitStartedAt = Date.now();
        this.sdkReadyPollId = setInterval(() => this.checkSdkReady(), SDK_READY_POLL_MS);
        this.sdkReadyTimeoutId = setTimeout(() => this.reportSlowSdk(), SDK_READY_TIMEOUT_MS);
    }

    /**
     * Marks the SDK ready and replays queued events once it accepts them.
     *
     * @returns {void}
     */
    checkSdkReady() {
        if (this.sdkReady || this.initFailed) return;
        if (!sdkAcceptsEvents()) return;

        this.stopWatchingSdk();
        this.sdkReady = true;

        const pending = this.pendingEvents;

        this.pendingEvents = [];
        pending.forEach(({ sender, args }) => this.runSender(sender, args));
    }

    /**
     * Reports an SDK that has not come up in time, and keeps waiting.
     *
     * The cause is usually a backgrounded tab or a slow /init (the SDK sets no
     * XHR timeout); rejected keys leave it permanently disabled. The queue is
     * kept either way: it is capped, and a late start still delivers it.
     * Polling just slows down so a dead SDK stays cheap.
     *
     * @returns {void}
     */
    reportSlowSdk() {
        if (this.sdkReady) return;

        this.sdkReadyTimeoutId = undefined;
        clearInterval(this.sdkReadyPollId);
        this.sdkReadyPollId = setInterval(() => this.checkSdkReady(), SDK_LATE_POLL_MS);

        logWarning(
            `GameAnalytics: SDK did not accept events within ${SDK_READY_TIMEOUT_MS}ms, ${this.pendingEvents.length} event(s) queued, still waiting`,
            { level: 'warning', tags: this.getSdkWaitTags() }
        );
    }

    /**
     * Describes the page state when the slow start is reported, as tags, to
     * tell a slow network from a page that was backgrounded or throttled.
     *
     * A large `ga_wait_elapsed` means timers ran late rather than /init being
     * slow. Consent is not reported: the service only exists once analytics
     * consent is granted, so every one of these reports already implies it.
     *
     * @returns {{ ga_wait_elapsed: string, ga_wait_visibility: string, ga_wait_online: string }}
     */
    getSdkWaitTags() {
        const elapsedMs = this.sdkWaitStartedAt ? Date.now() - this.sdkWaitStartedAt : undefined;
        const online = typeof navigator === 'undefined' ? undefined : navigator.onLine;

        return {
            ga_wait_elapsed: elapsedBucket(elapsedMs),
            ga_wait_visibility:
                typeof document === 'undefined' ? 'unknown' : document.visibilityState,
            ga_wait_online: online === undefined ? 'unknown' : online ? 'yes' : 'no',
        };
    }

    /**
     * Clears the readiness poll, its deadline and the unload hook.
     *
     * @returns {void}
     */
    stopWatchingSdk() {
        clearInterval(this.sdkReadyPollId);
        clearTimeout(this.sdkReadyTimeoutId);
        this.sdkReadyPollId = undefined;
        this.sdkReadyTimeoutId = undefined;

        if (!this.beforeUnloadListener) return;

        gameanalytics.GameAnalytics.removeOnBeforeUnloadListener(this.beforeUnloadListener);
        this.beforeUnloadListener = undefined;
    }

    /**
     * Releases everything this instance owns, without touching the SDK.
     *
     * Use it when the provider drops the service for a reason other than
     * consent - the SDK is a module-level singleton that outlives us.
     *
     * @returns {void}
     */
    destroy() {
        this.stopWatchingSdk();
        this.sdkReady = false;
        this.pendingEvents = [];
        this.droppedEvents = 0;
    }

    /**
     * Stops analytics because consent was withdrawn.
     *
     * `setEnabledEventSubmission(false)` is the SDK's only kill switch - it
     * guards both recording an event and the loop that transmits them - and
     * there is no uninit. Disabling before ending the session keeps
     * `endSession` from recording a session end the user no longer consents
     * to, while still clearing the session so a later re-grant opens a fresh
     * one.
     *
     * @returns {void}
     */
    stop() {
        this.destroy();

        gameanalytics.GameAnalytics.setEnabledEventSubmission(false);
        gameanalytics.GameAnalytics.endSession();
        purgeStoredEvents(this.gameKey);
    }

    /**
     * Sends a call to the SDK, or queues it until the SDK accepts events.
     *
     * Call it after a method's own validation so invalid payloads are still
     * reported straight away.
     *
     * @param {keyof SDK_SENDERS} sender - Which SDK call to make.
     * @param {unknown[]} args - Arguments to make it with.
     * @returns {void}
     */
    send(sender, args) {
        if (this.sdkReady) {
            this.runSender(sender, args);
            return;
        }
        if (this.pendingEvents.length < PENDING_EVENTS_LIMIT) {
            this.pendingEvents.push({ sender, args });
            return;
        }

        this.droppedEvents += 1;

        // The counter doubles as the once-guard, so a stuck init reports the
        // overflow rather than discarding hundreds of events in silence.
        if (this.droppedEvents > 1) return;

        logWarning(
            `Game Analytics: pending queue hit its ${PENDING_EVENTS_LIMIT} event limit before the SDK was ready`,
            { level: 'warning', tags: this.getSdkWaitTags() }
        );
    }

    /**
     * Hands a call to the SDK, isolating any failure.
     *
     * Keeps a throw from escaping into a Unity event handler, and - during a
     * flush - from discarding every call still queued behind it.
     *
     * @param {keyof SDK_SENDERS} sender - Which SDK call to make.
     * @param {unknown[]} args - Arguments to make it with.
     * @returns {void}
     */
    runSender(sender, args) {
        try {
            SDK_SENDERS[sender](...args);
        } catch (error) {
            captureCustomException(error, 'GameAnalyticsSendFailed', {
                sender,
                landing: this.landingName,
            });
        }
    }

    /**
     * Sends a design event to GameAnalytics.
     *
     * @param {string} eventName - Event name.
     * @param {number} value - Numeric value for the event.
     * @param {string} fieldsJson - Additional fields as JSON string.
     * @param {string} eventSource - Event source name.
     * @returns {void}
     */
    sendDesignEvent(eventName, value = undefined, fieldsJson = undefined, eventSource = 'LP') {
        if (this.initFailed) return;
        if (!isNonEmptyString(eventName)) {
            logWarning(
                `[Game Analytics ${this.landingName}]: ${eventSource} Design event invalid. Missing eventName param.`
            );
            return;
        }

        const customFields = parseCustomFields(
            eventSource,
            this.landingName,
            eventName,
            fieldsJson
        );

        this.send('design', [eventName, value, customFields]);
    }

    /**
     * Sends a progression event.
     *
     * @param {"Start"|"Fail"|"Complete"|"Undefined"} progressionStatus - Progression status.
     * @param {string} [progression1] - Main progression field.
     * @param {string} [progression2] - Secondary progression field.
     * @param {string} [progression3] - Tertiary progression field.
     * @param {number} [score] - Event score.
     * @returns {void}
     */
    sendProgressionEvent(progressionStatus, progression1, progression2, progression3, score) {
        if (this.initFailed) return;
        const paramsValid = areProgressionParamsValid(
            this.landingName,
            progressionStatus,
            progression1,
            progression2,
            progression3
        );
        if (!paramsValid) return;

        this.send('progression', [
            progressionStatus,
            progression1,
            progression2,
            progression3,
            score,
        ]);
    }

    /**
     * Sends a resource event.
     *
     * @param {"Undefined"|"Source"|"Sink"} flowType - Resource flow type.
     * @param {string} currency - Currency name.
     * @param {number} amount - Amount of currency.
     * @param {string} itemType - Item type.
     * @param {string} itemId - Item identifier.
     * @returns {void}
     */
    sendResourceEvent(flowType, currency, amount, itemType, itemId) {
        if (this.initFailed) return;
        const paramsValid = areResourceParamsValid(
            this.landingName,
            flowType,
            currency,
            amount,
            itemType,
            itemId
        );
        if (!paramsValid) return;

        this.send('resource', [flowType, currency, amount, itemType, itemId]);
    }

    /**
     * Sends an ad event.
     *
     * @param {"Show"|"FailedShow"|"RewardReceived"|"Clicked"|"Undefined"} eventType - Ad event type.
     * @param {"RewardedVideo"|"Banner"|"Video"|"Undefined"} adType - Ad type.
     * @param {string} adSdkName - Ad provider name.
     * @param {string} adPlacement - Ad placement within the game.
     * @param {Object} [customFields] - Additional custom fields.
     * @returns {void}
     */
    sendAdEvent(eventType, adType, adSdkName, adPlacement, customFields) {
        if (this.initFailed) return;

        this.send('ad', [eventType, adType, adSdkName, adPlacement, customFields]);
    }

    /**
     * Sends an error event.
     *
     * @param {"Error"|"Warning"|"Info"|"Debug"|"Critical"|"Undefined"} errorType - Error severity.
     * @param {string} message - Error message.
     * @returns {void}
     */
    sendErrorEvent(errorType, message) {
        if (this.initFailed) return;

        this.send('error', [errorType, message]);
    }

    /**
     * Starts a GameAnalytics session.
     *
     * Never queued: replaying it once the SDK is ready would end the session
     * `init()` had already opened and pay for a second /init round-trip. The
     * SDK ignores it until it is initialized, by which point its own session
     * is running anyway.
     *
     * @returns {void}
     */
    startGameSession() {
        if (this.initFailed) return;

        this.runSender('startSession', []);
    }

    /**
     * Ends a GameAnalytics session.
     *
     * Never queued, for the same reason as {@link startGameSession}.
     *
     * @returns {void}
     */
    endGameSession() {
        if (this.initFailed) return;

        this.runSender('endSession', []);
    }

    /**
     * Sets custom dimension 1 for the player.
     *
     * Queued like an event so a dimension change cannot get ahead of the
     * events it is meant to annotate.
     *
     * @param {string} value - Dimension 1 value.
     * @returns {void}
     */
    setCustomDimension1(value) {
        if (this.initFailed) return;

        this.send('dimension1', [value]);
    }

    /**
     * Sets custom dimension 2 for the player.
     * @param {string} value - Dimension 2 value.
     * @returns {void}
     */
    setCustomDimension2(value) {
        if (this.initFailed) return;

        this.send('dimension2', [value]);
    }

    /**
     * Sets custom dimension 3 for the player.
     * @param {string} value - Dimension 3 value.
     * @returns {void}
     */
    setCustomDimension3(value) {
        if (this.initFailed) return;

        this.send('dimension3', [value]);
    }
}
